import {
  TOLERANCE_TYPES,
  type CreateRecipeRequest,
  type ItemDto,
  type RecipeComponentDto,
  type RecipeDto,
  type RecipeStatus,
  type ToleranceType,
} from '@gsls/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ErrorNote,
  Field,
  InfoNote,
  PageHeader,
  Spinner,
  inputClass,
} from '../components/ui';
import { CodeField } from '../components/CodeField';
import { DataTable, type DataColumn } from '../components/DataTable';
import { FilterBar } from '../components/FilterBar';
import { MultiCheckboxList } from '../components/MultiCheckboxList';
import { ApiError, api } from '../lib/api';
import { formatQuantity } from '../lib/format';
import { Can } from '../lib/session';

const STATUS_TONES: Record<RecipeStatus, 'green' | 'accent' | 'muted'> = {
  draft: 'accent',
  approved: 'green',
  retired: 'muted',
};

interface ComponentDraft {
  key: string;
  itemId: string;
  quantity: string;
  uom: string;
  toleranceType: '' | ToleranceType;
  toleranceValue: string;
  isOptional: boolean;
  note: string;
  substituteItemIds: string[];
}

function emptyComponent(): ComponentDraft {
  return {
    key: Math.random().toString(36).slice(2),
    itemId: '',
    quantity: '',
    uom: '',
    toleranceType: '',
    toleranceValue: '',
    isOptional: false,
    note: '',
    substituteItemIds: [],
  };
}

/**
 * Recipes / bill of materials.
 *
 * A draft is editable. An approved version is not — revising it means creating
 * the next version, so a batch that pinned v3 still shows what it was actually
 * made from even after v4 exists.
 */
export function RecipesPage() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RecipeDto | null>(null);
  const [approveNote, setApproveNote] = useState('');

  const recipes = useQuery({
    queryKey: ['recipes', { search, statusFilter, showAllVersions }],
    queryFn: () =>
      api.recipes.list({
        search,
        latestOnly: !showAllVersions,
        ...(statusFilter ? { status: statusFilter } : {}),
        limit: 100,
      }),
  });

  const items = useQuery({
    queryKey: ['items', { limit: 200 }],
    queryFn: () => api.items.list({ limit: 200 }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['recipes'] });

  const action = useMutation({
    mutationFn: async (variables: {
      kind: 'approve' | 'new-version' | 'retire' | 'delete';
      recipe: RecipeDto;
      note?: string;
    }) => {
      switch (variables.kind) {
        case 'approve':
          await api.recipes.approve(variables.recipe.id, variables.note);
          return;
        case 'new-version':
          await api.recipes.newVersion(variables.recipe.id);
          return;
        case 'retire':
          await api.recipes.retire(variables.recipe.id);
          return;
        case 'delete':
          await api.recipes.remove(variables.recipe.id);
          return;
      }
    },
    onSuccess: (_result, variables) => {
      void invalidate();
      setApproveNote('');
      if (variables.kind === 'delete') setSelectedId(null);
    },
  });

  const selected = recipes.data?.items.find((recipe) => recipe.id === selectedId) ?? null;
  const componentItems = items.data?.items ?? [];

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
    setSelectedId(null);
    action.reset();
  }

  function openEdit(recipe: RecipeDto) {
    setEditing(recipe);
    setFormOpen(true);
    setSelectedId(null);
    action.reset();
  }

  const recipeColumns: DataColumn<RecipeDto>[] = [
    {
      key: 'code',
      header: 'Code',
      cell: (recipe) => <span className="font-mono text-xs">{recipe.code}</span>,
    },
    { key: 'version', header: 'Ver', cell: (recipe) => <span className="font-semibold">v{recipe.version}</span> },
    { key: 'name', header: 'Name', primary: true, cell: (recipe) => <span className="font-medium text-ink">{recipe.name}</span> },
    {
      key: 'produces',
      header: 'Produces',
      cell: (recipe) => (
        <>
          <span className="font-mono text-xs">{recipe.itemCode}</span>
          <br />
          <span className="text-ink-body">{recipe.itemName}</span>
        </>
      ),
    },
    {
      key: 'output',
      header: 'Output',
      align: 'right',
      cell: (recipe) => (
        <span className="whitespace-nowrap">
          {formatQuantity(recipe.outputQuantity, recipe.outputUom)}
        </span>
      ),
    },
    { key: 'components', header: 'Components', cell: (recipe) => recipe.componentCount },
    {
      key: 'status',
      header: 'Status',
      cell: (recipe) => <Badge tone={STATUS_TONES[recipe.status]}>{recipe.status}</Badge>,
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Master data"
        title="Recipes"
        description="What each product is made from, and how much of each input a batch should draw. Approving a version freezes it — a later revision becomes a new version, so the record of what was made never changes underneath you."
        actions={
          <Can capability="masterdata.manage">
            <Button onClick={openCreate}>New recipe</Button>
          </Can>
        }
      />

      {formOpen ? (
        <RecipeForm
          editing={editing}
          items={componentItems}
          onClose={() => setFormOpen(false)}
          onSaved={() => {
            void invalidate();
            setFormOpen(false);
            setEditing(null);
          }}
        />
      ) : null}

      {action.error ? (
        <div className="mb-6">
          <ErrorNote>
            {action.error instanceof ApiError ? action.error.message : 'That action failed.'}
          </ErrorNote>
        </div>
      ) : null}

      {selected ? (
        <RecipeDetail
          recipe={selected}
          approveNote={approveNote}
          onApproveNoteChange={setApproveNote}
          onEdit={() => openEdit(selected)}
          onAction={(kind) =>
            action.mutate({
              kind,
              recipe: selected,
              ...(kind === 'approve' && approveNote.trim() ? { note: approveNote.trim() } : {}),
            })
          }
          busy={action.isPending}
          onClose={() => setSelectedId(null)}
        />
      ) : null}

      <Card>
        <CardBody>
          <FilterBar activeCount={(search ? 1 : 0) + (statusFilter ? 1 : 0) + (showAllVersions ? 1 : 0)}>
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <input
                name="recipeSearch"
                className={`${inputClass} max-w-xs`}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search code, name or product"
                aria-label="Search recipes"
              />
              <select
                name="recipeStatus"
                className={`${inputClass} max-w-[12rem]`}
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label="Filter by status"
              >
                <option value="">Any status</option>
                <option value="draft">Draft</option>
                <option value="approved">Approved</option>
                <option value="retired">Retired</option>
              </select>
              <label className="flex items-center gap-2 text-sm text-ink-body">
                <input
                  name="allVersions"
                  type="checkbox"
                  checked={showAllVersions}
                  onChange={(event) => setShowAllVersions(event.target.checked)}
                />
                Show every version
              </label>
              {recipes.isSuccess ? (
                <span className="text-sm text-ink-muted">
                  {recipes.data.total} recipe{recipes.data.total === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
          </FilterBar>

          {recipes.isPending ? <Spinner /> : null}
          <ErrorNote>
            {recipes.isError ? 'Could not load recipes. Is the API running?' : ''}
          </ErrorNote>

          {recipes.isSuccess ? (
            <DataTable
              rows={recipes.data.items}
              columns={recipeColumns}
              rowKey={(recipe) => recipe.id}
              rowClassName={() => 'hover:bg-brand-soft/40'}
              actions={(recipe) => (
                <Button tone="ghost" onClick={() => setSelectedId(recipe.id)}>
                  Open
                </Button>
              )}
              empty="No recipes yet. A recipe says what a product is made from and in what quantities."
            />
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Detail                                                                      */
/* -------------------------------------------------------------------------- */

const COMPONENT_COLUMNS: DataColumn<RecipeComponentDto>[] = [
  {
    key: 'input',
    header: 'Input',
    primary: true,
    cell: (component) => (
      <>
        <span className="font-mono text-xs">{component.itemCode}</span>
        <br />
        <span className="text-ink-body">{component.itemName}</span>
      </>
    ),
  },
  {
    key: 'quantity',
    header: 'Quantity',
    align: 'right',
    cell: (component) => (
      <span className="whitespace-nowrap font-semibold text-ink">
        {formatQuantity(component.quantity, component.uom)}
      </span>
    ),
  },
  {
    key: 'range',
    header: 'Allowed range',
    cell: (component) => (
      <span className="whitespace-nowrap">
        {component.minQuantity && component.maxQuantity
          ? `${component.minQuantity} – ${component.maxQuantity} ${component.uom}`
          : 'exact only'}
      </span>
    ),
  },
  {
    key: 'tolerance',
    header: 'Tolerance',
    cell: (component) =>
      component.toleranceType === null ? (
        <Badge tone="muted">none</Badge>
      ) : (
        <Badge tone={component.toleranceType === 'percent' ? 'green' : 'accent'}>
          ±{component.toleranceValue}
          {component.toleranceType === 'percent' ? '%' : ` ${component.uom}`}
        </Badge>
      ),
  },
  { key: 'optional', header: 'Optional', cell: (component) => (component.isOptional ? 'Yes' : '—') },
  {
    key: 'substitutes',
    header: 'Substitutes',
    cell: (component) => (
      <span className="text-xs">
        {component.substitutes.length === 0
          ? '—'
          : component.substitutes.map((substitute) => substitute.itemCode).join(', ')}
      </span>
    ),
  },
];

function RecipeDetail({
  recipe,
  approveNote,
  onApproveNoteChange,
  onEdit,
  onAction,
  busy,
  onClose,
}: {
  recipe: RecipeDto;
  approveNote: string;
  onApproveNoteChange: (value: string) => void;
  onEdit: () => void;
  onAction: (kind: 'approve' | 'new-version' | 'retire' | 'delete') => void;
  busy: boolean;
  onClose: () => void;
}) {
  return (
    <Card className="mb-6 border-l-4 border-l-brand">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-deep">
              {recipe.code} · version {recipe.version}
            </p>
            <h2 className="mt-1 text-lg font-bold text-ink">{recipe.name}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONES[recipe.status]}>{recipe.status}</Badge>
              <span className="text-sm text-ink-body">
                produces {formatQuantity(recipe.outputQuantity, recipe.outputUom)} of{' '}
                <span className="font-mono text-xs">{recipe.itemCode}</span>
              </span>
            </div>
            {recipe.status === 'approved' && recipe.approvedAt ? (
              <p className="mt-2 text-xs text-ink-muted">
                Approved {recipe.approvedAt.slice(0, 10)}
                {recipe.approvedByName ? ` by ${recipe.approvedByName}` : ''}. This version is
                frozen: revision means a new version.
              </p>
            ) : null}
          </div>

          <Can capability="masterdata.manage">
            <div className="flex flex-wrap items-end gap-2">
              {recipe.status === 'draft' ? (
                <>
                  <Button tone="ghost" onClick={onEdit}>
                    Edit draft
                  </Button>
                  <Button disabled={busy} onClick={() => onAction('approve')}>
                    {busy ? 'Working…' : 'Approve'}
                  </Button>
                </>
              ) : null}
              {recipe.status === 'approved' ? (
                <>
                  <Button disabled={busy} onClick={() => onAction('new-version')}>
                    New version
                  </Button>
                  <Button tone="danger" disabled={busy} onClick={() => onAction('retire')}>
                    Retire
                  </Button>
                </>
              ) : null}
              {recipe.status === 'retired' ? (
                <Button disabled={busy} onClick={() => onAction('new-version')}>
                  Revive as new version
                </Button>
              ) : null}
              {recipe.status === 'draft' ? (
                <Button tone="danger" disabled={busy} onClick={() => onAction('delete')}>
                  Delete draft
                </Button>
              ) : null}
              <Button tone="ghost" onClick={onClose}>
                Close
              </Button>
            </div>
          </Can>
        </div>

        {recipe.status === 'draft' ? (
          <div className="mt-4 max-w-xl">
            <Field
              label="Approval note"
              hint="Optional, recorded in the audit trail"
              tooltip="Approving freezes this version: it can no longer be edited, and it becomes the one production runs against. Revising it later creates the next version, so batches made against this one still show exactly what they were made from."
            >
              <input
                name="approvalNote"
                className={inputClass}
                value={approveNote}
                onChange={(event) => onApproveNoteChange(event.target.value)}
                placeholder="Why this formulation is being approved"
              />
            </Field>
          </div>
        ) : null}

        {recipe.notes ? (
          <p className="mt-4 text-sm text-ink-body">{recipe.notes}</p>
        ) : null}

        <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-ink-muted">
          Components ({recipe.componentCount})
        </h3>
        <div className="mt-2">
          <DataTable
            rows={recipe.components ?? []}
            columns={COMPONENT_COLUMNS}
            rowKey={(component) => component.id}
          />
        </div>
      </CardBody>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Create / edit form                                                          */
/* -------------------------------------------------------------------------- */

function RecipeForm({
  editing,
  items,
  onClose,
  onSaved,
}: {
  editing: RecipeDto | null;
  items: ItemDto[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(editing?.code ?? '');
  const [name, setName] = useState(editing?.name ?? '');
  const [itemId, setItemId] = useState(editing?.itemId ?? '');
  const [outputQuantity, setOutputQuantity] = useState(editing?.outputQuantity ?? '');
  const [outputUom, setOutputUom] = useState(editing?.outputUom ?? '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [components, setComponents] = useState<ComponentDraft[]>(
    editing?.components?.length
      ? editing.components.map((component) => ({
          key: component.id,
          itemId: component.itemId,
          quantity: component.quantity,
          uom: component.uom,
          toleranceType: component.toleranceType ?? '',
          toleranceValue: component.toleranceValue ?? '',
          isOptional: component.isOptional,
          note: component.note ?? '',
          substituteItemIds: component.substitutes.map((substitute) => substitute.itemId),
        }))
      : [emptyComponent()],
  );

  const productItems = items.filter(
    (item) => item.kind === 'finished_good' || item.kind === 'intermediate',
  );

  const save = useMutation({
    mutationFn: async () => {
      const payloadComponents = components.map((component) => ({
        itemId: component.itemId,
        quantity: component.quantity.trim(),
        uom: component.uom,
        toleranceType: component.toleranceType === '' ? null : component.toleranceType,
        toleranceValue:
          component.toleranceType === '' || component.toleranceValue.trim() === ''
            ? null
            : component.toleranceValue.trim(),
        isOptional: component.isOptional,
        ...(component.note.trim() ? { note: component.note.trim() } : {}),
        substituteItemIds: component.substituteItemIds,
      }));

      if (editing) {
        await api.recipes.update(editing.id, {
          name: name.trim(),
          outputQuantity: outputQuantity.trim(),
          outputUom,
          notes: notes.trim() || null,
          components: payloadComponents,
        });
        return;
      }

      const body: CreateRecipeRequest = {
        code: code.trim(),
        name: name.trim(),
        itemId,
        outputQuantity: outputQuantity.trim(),
        outputUom,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        components: payloadComponents,
      };
      await api.recipes.create(body);
    },
    onSuccess: onSaved,
  });

  function updateComponent(key: string, patch: Partial<ComponentDraft>) {
    setComponents((current) =>
      current.map((component) => (component.key === key ? { ...component, ...patch } : component)),
    );
  }

  function pickItem(key: string, nextItemId: string) {
    const item = items.find((candidate) => candidate.id === nextItemId);
    updateComponent(key, {
      itemId: nextItemId,
      // Default the unit to the input's own base unit; the API validates it either way.
      uom: item?.baseUom ?? '',
      substituteItemIds: [],
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate();
  }

  const selectedProduct = items.find((item) => item.id === itemId);

  return (
    <Card className="mb-6">
      <CardBody>
        <h2 className="text-base font-bold text-ink">
          {editing ? `Edit ${editing.code} v${editing.version}` : 'New recipe'}
        </h2>
        <p className="mt-1 text-sm font-light text-ink-body">
          Quantities are stated for one output batch, e.g. “100 kg of this product is made from
          50 kg of that input”.
        </p>

        <form autoComplete="off" onSubmit={handleSubmit} className="mt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <CodeField
              label="Recipe code"
              value={code}
              onChange={setCode}
              queryKey={['recipe-suggest', name.trim()]}
              queryFn={
                !editing && name.trim().length >= 2
                  ? () => api.recipes.suggestCode({ name: name.trim() })
                  : undefined
              }
              disabled={Boolean(editing)}
              hint={editing ? 'Codes are immutable' : undefined}
              placeholder="RCP-CRY-001"
              tooltip="Built from the recipe's name and a sequence number — “API Crystallisation” suggests RCP-CRY-001. The version is tracked separately, so revising an approved recipe never changes its code. Accept the suggestion or type your own."
              error={
                !editing && save.error instanceof ApiError && save.error.status === 409
                  ? save.error.message
                  : undefined
              }
            />

            <Field label="Name">
              <input
                name="recipeName"
                className={inputClass}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                placeholder="Crystalline API, standard route"
              />
            </Field>

            <Field
              label="Produces"
              hint="Only finished goods and intermediates"
              tooltip="The product this recipe makes. A batch of it will produce exactly one lot of this item, priced at the cost of everything the batch consumed."
            >
              <select
                name="productItem"
                className={inputClass}
                value={itemId}
                onChange={(event) => {
                  setItemId(event.target.value);
                  const product = items.find((candidate) => candidate.id === event.target.value);
                  if (product && !outputUom) setOutputUom(product.baseUom);
                }}
                required
                disabled={Boolean(editing)}
              >
                <option value="">Select the product…</option>
                {productItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.code} — {item.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Output quantity"
              tooltip="The size of batch these component quantities are written for — “100 kg is made from 50 kg of that”. Running a smaller or larger batch scales every component automatically."
            >
              <div className="flex gap-2">
                <input
                  name="outputQuantity"
                  className={inputClass}
                  value={outputQuantity}
                  onChange={(event) => setOutputQuantity(event.target.value)}
                  required
                  placeholder="100"
                  inputMode="decimal"
                />
                <input
                  name="outputUom"
                  className={`${inputClass} max-w-[7rem]`}
                  value={outputUom}
                  onChange={(event) => setOutputUom(event.target.value)}
                  required
                  placeholder={selectedProduct?.baseUom ?? 'kg'}
                  aria-label="Output unit of measure"
                />
              </div>
            </Field>

            <div className="sm:col-span-2">
              <Field label="Notes" hint="Optional">
                <input
                  name="notes"
                  className={inputClass}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Anything a production operator should know"
                />
              </Field>
            </div>
          </div>

          <h3 className="mt-6 text-sm font-bold uppercase tracking-wide text-ink-muted">
            Components
          </h3>

          <div className="mt-2 space-y-3">
            {components.map((component, index) => {
              const componentItem = items.find((item) => item.id === component.itemId);
              const availableUnits = componentItem?.units?.map((unit) => unit.uom) ?? [];
              const substituteCandidates = items.filter(
                (candidate) =>
                  candidate.id !== component.itemId &&
                  candidate.id !== itemId &&
                  candidate.isActive,
              );

              return (
                <div
                  key={component.key}
                  className="rounded-md border border-surface-muted bg-canvas p-4"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wide text-ink-muted">
                      Line {index + 1}
                    </p>
                    {components.length > 1 ? (
                      <button
                        type="button"
                        className="text-xs font-bold uppercase tracking-wide text-red-700 hover:underline"
                        onClick={() =>
                          setComponents((current) =>
                            current.filter((candidate) => candidate.key !== component.key),
                          )
                        }
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>

                  <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Field label="Input item">
                      <select
                        className={inputClass}
                        value={component.itemId}
                        onChange={(event) => pickItem(component.key, event.target.value)}
                        required
                        aria-label={`Line ${index + 1} input item`}
                      >
                        <option value="">Select an input…</option>
                        {items.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.code} — {item.name}
                          </option>
                        ))}
                      </select>
                    </Field>

                    <Field label="Quantity">
                      <div className="flex gap-2">
                        <input
                          name={`component-quantity-${index}`}
                          className={inputClass}
                          value={component.quantity}
                          onChange={(event) =>
                            updateComponent(component.key, { quantity: event.target.value })
                          }
                          required
                          inputMode="decimal"
                          placeholder="50"
                          aria-label={`Line ${index + 1} quantity`}
                        />
                        <select
                          name={`component-uom-${index}`}
                          className={`${inputClass} max-w-[6rem]`}
                          value={component.uom}
                          onChange={(event) =>
                            updateComponent(component.key, { uom: event.target.value })
                          }
                          required
                          aria-label={`Line ${index + 1} unit of measure`}
                        >
                          {availableUnits.length === 0 ? <option value="">—</option> : null}
                          {availableUnits.map((unit) => (
                            <option key={unit} value={unit}>
                              {unit}
                            </option>
                          ))}
                        </select>
                      </div>
                    </Field>

                    <Field
                      label="Tolerance"
                      hint="How much the drawn quantity may vary"
                      tooltip="How far a batch's total draw for this line may stray before it needs authorising. A percentage suits bulk material; a plain quantity suits countable things like bottles. Drawing less than planned is normal while a batch is running — only going above the limit needs a reason."
                    >
                      <div className="flex gap-2">
                        <select
                          className={inputClass}
                          value={component.toleranceType}
                          onChange={(event) =>
                            updateComponent(component.key, {
                              toleranceType: event.target.value as '' | ToleranceType,
                              toleranceValue: event.target.value === '' ? '' : component.toleranceValue,
                            })
                          }
                          aria-label={`Line ${index + 1} tolerance type`}
                        >
                          <option value="">Exact only</option>
                          {TOLERANCE_TYPES.map((type) => (
                            <option key={type} value={type}>
                              ± {type === 'percent' ? 'percent' : 'quantity'}
                            </option>
                          ))}
                        </select>
                        {component.toleranceType !== '' ? (
                          <input
                            name={`component-tolerance-${index}`}
                            className={`${inputClass} max-w-[6rem]`}
                            value={component.toleranceValue}
                            onChange={(event) =>
                              updateComponent(component.key, { toleranceValue: event.target.value })
                            }
                            required
                            inputMode="decimal"
                            placeholder={component.toleranceType === 'percent' ? '2' : '0.5'}
                            aria-label={`Line ${index + 1} tolerance value`}
                          />
                        ) : null}
                      </div>
                    </Field>

                    <Field
                      label="Substitutes"
                      hint="Items that may be drawn instead"
                      tooltip="Alternatives a batch may draw in place of this input, for example when the usual material is out of stock. Drawing one is allowed but always needs a reason, and the batch records that it was a substitute."
                    >
                      <MultiCheckboxList
                        options={substituteCandidates.map((candidate) => ({
                          value: candidate.id,
                          label: `${candidate.code} — ${candidate.name}`,
                        }))}
                        selected={component.substituteItemIds}
                        onChange={(next) =>
                          updateComponent(component.key, { substituteItemIds: next })
                        }
                        emptyLabel="No other items to substitute."
                      />
                    </Field>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-ink-body">
                      <input
                        type="checkbox"
                        checked={component.isOptional}
                        onChange={(event) =>
                          updateComponent(component.key, { isOptional: event.target.checked })
                        }
                        name={`optional-${component.key}`}
                      />
                      Optional component
                    </label>
                    <input
                      name={`component-note-${index}`}
                      className={`${inputClass} max-w-md`}
                      value={component.note}
                      onChange={(event) =>
                        updateComponent(component.key, { note: event.target.value })
                      }
                      placeholder="Line note (optional)"
                      aria-label={`Line ${index + 1} note`}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-3">
            <Button
              tone="ghost"
              onClick={() => setComponents((current) => [...current, emptyComponent()])}
            >
              Add component
            </Button>
          </div>

          {save.error ? (
            <div className="mt-4">
              <ErrorNote>
                {save.error instanceof ApiError ? save.error.message : 'Could not save the recipe.'}
              </ErrorNote>
            </div>
          ) : null}

          <div className="mt-4">
            <InfoNote>
              Saving creates or updates a <strong>draft</strong>. Approving it freezes this version;
              any later change becomes the next version.
            </InfoNote>
          </div>

          <div className="mt-4 flex gap-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : editing ? 'Save draft' : 'Create draft'}
            </Button>
            <Button tone="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
